import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  TextField,
  Radio,
  List,
  ListItem,
  ListItemText,
  Grid,
  CircularProgress,
  IconButton,
} from '@material-ui/core';
import { Field, change } from 'redux-form/immutable';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import _ from 'lodash';
import Button from '@material-ui/core/Button';
import AddIcon from '@mui/icons-material/Add';
import FormLabel from '@material-ui/core/FormLabel';
import DeleteIcon from '@mui/icons-material/Delete';

import FormFieldLabel from 'retired/FormFieldLabel';
import LocationSelector from 'retired/LocationSelector';
import SelectableChip from 'retired/SelectableChip';
import OrganizationApi from 'entities/organization/api';
import { Privileges } from 'utils/privileges';
import { truthy } from 'utils/truthy';
import { NonprofitIdentifierType } from 'generated/graphql';

import { NewPartner } from './NewPartner';
import { SelectedNonprofit } from './SelectedNonprofit';

const HiddenInput = ({ input }) => (
  <input name={input.name} style={{ height: 0, width: 0, display: 'block' }} />
);
HiddenInput.propTypes = {
  input: PropTypes.object,
};

const UnsetRadio = ({ input: { onChange }, label, disabled }) => (
  <SelectableChip
    onClick={() => onChange(null)}
    label={label}
    disabled={disabled}
    style={{ margin: 0 }}
  />
);
UnsetRadio.propTypes = {
  input: PropTypes.object,
  label: PropTypes.string,
  disabled: PropTypes.bool,
};

const ListFormRadio = ({ input: { onChange, checked, value = null, name }, partner }) => {
  const ncesIdentifier = partner.identifiers?.find(
    (identifier) => identifier.type === NonprofitIdentifierType.Nces
  );
  const identifier = partner.ein
    ? ` – EIN: ${partner.ein}`
    : ncesIdentifier
    ? ` – NCES ID: ${ncesIdentifier.id}`
    : null;
  return (
    <ListItem button onClick={() => onChange(value)}>
      <Radio checked={checked} tabIndex={-1} color="primary" name={name} />
      <ListItemText
        primary={partner.name}
        secondary={[
          partner.city && partner.state
            ? `${partner.city}, ${partner.state}`
            : partner.locationObject?.city &&
              partner.locationObject?.state &&
              `${partner.locationObject.city}, ${partner.locationObject.state}`,
          identifier,
        ]
          .filter(truthy)
          .join(' ')}
      />
    </ListItem>
  );
};
ListFormRadio.propTypes = {
  input: PropTypes.object,
  partner: PropTypes.object,
};

const FormPartnerSelector = (props) => {
  const {
    user,
    isCompany = user?.organization?.isCompany(),
    required = true,
    partners: allPartners,
    partner,
    partnerName,
    partnerEmail,
    fetchPartnersAction,
    partnersLoading,
    organization,
    acceptsDonations,
    selectedOrganization,
    initialSelectedLocation = isCompany
      ? selectedOrganization?.locationObject?.countryCode ??
        user?.organization?.locationObject?.countryCode
      : '',
    initialSelectedSearchTerm = '',
  } = props;

  const dispatch = useDispatch();

  useEffect(() => {
    fetchPartnersAction();
  }, []);

  const validateOrganization = useCallback(
    (value, allValues) => {
      if (!isCompany) {
        return undefined;
      }
      const formValues = allValues.toJS();
      if (formValues.partnerName && formValues.partnerEmail && Boolean(value)) {
        return 'Please delete the selected partner to create a new one';
      }
      if (!formValues.partnerName && !formValues.partnerEmail && !value && required) {
        return 'Please select or create a partner';
      }
      return undefined;
    },
    [isCompany]
  );

  const [selectedLocation, setSelectedLocation] = useState(initialSelectedLocation);
  const [searchValue, setSearchValue] = useState(initialSelectedSearchTerm);
  const [nonPartnerNonprofits, setNonPartnerNonprofits] = useState([]);
  const [newPartnerNonprofitSelected, setNewPartnerNonprofitSelected] = useState(false);
  const firstLoad = useRef(true);
  const [nonprofitsLoading, setNonprofitsLoading] = useState(false);
  const [selectedNonprofit, setSelectedNonprofit] = useState(null);

  const partners = searchValue
    ? allPartners.filter((p) => p.searchTerm === searchValue)
    : allPartners;

  const updateOptions = useCallback(
    _.debounce((value, location, passedAcceptsDonations, partnersList) => {
      if (isCompany && value.length > 2) {
        setNonprofitsLoading(true);

        const searchNonprofitDB = user?.organization?.settings?.get('searchNonprofitDB') || false;

        OrganizationApi.searchNonprofits(
          {
            searchTerm: value,
            countryCode: location,
            acceptsDonations: passedAcceptsDonations,
          },
          searchNonprofitDB
        ).subscribe((results) => {
          setNonprofitsLoading(false);
          const filteredResults = results.filter(
            (nonPartnerNonprofit) =>
              !partnersList.find(
                (partnerNonprofit) => partnerNonprofit.name === nonPartnerNonprofit.name
              )
          );
          setNonPartnerNonprofits(filteredResults);
        });
      }
    }, 600),
    []
  );

  const fetchPartners = useCallback(
    _.debounce(
      (value, location, passedAcceptsDonations) =>
        fetchPartnersAction(value, location, passedAcceptsDonations),
      600
    ),
    []
  );

  useEffect(() => {
    fetchPartners(searchValue, selectedLocation);

    if (selectedLocation) {
      updateOptions(searchValue, selectedLocation, acceptsDonations, partners);
    }
  }, [searchValue, selectedLocation]);

  useEffect(() => {
    const value = isCompany ? organization.input.value : partner.input.value;
    // On create event page load
    if (firstLoad.current && !selectedOrganization) {
      firstLoad.current = false;
    }
    // On edit event page load: this is for initial load since the value is string and we need objects to display selected nonprofit
    if (firstLoad.current && selectedOrganization) {
      firstLoad.current = false;
      const partnerNonprofit = partners?.get(value);
      if (!partnerNonprofit) {
        setSelectedNonprofit(selectedOrganization);
        setNonPartnerNonprofits([selectedOrganization]);
      } else {
        setSelectedNonprofit(partnerNonprofit);
      }
    }
    if (!firstLoad.current) {
      if (value && typeof value === 'string') {
        const partnerNonprofit = partners?.get(value);
        if (partnerNonprofit) {
          setSelectedNonprofit(partnerNonprofit);
        }
      } else {
        setSelectedNonprofit(value);
      }
    }
  }, [organization, partner, partners, selectedOrganization]);

  return (
    <>
      <Grid container spacing={3}>
        <Grid item md={6}>
          {isCompany && (
            <Grid item style={{ marginBottom: 10 }}>
              <LocationSelector
                onSelect={(location) => setSelectedLocation(location)}
                selectedLocation={selectedLocation}
              />
            </Grid>
          )}
          <Grid container justifyContent="space-between">
            <Grid item style={{ width: '80%' }}>
              <FormControl style={{ width: '100%', height: 17 }}>
                <InputLabel shrink>
                  {isCompany
                    ? 'Choose your nonprofit'
                    : 'Choose a partner if this is a corporate deed'}
                </InputLabel>
              </FormControl>
            </Grid>
            {!isCompany && (
              <Grid item>
                <Field
                  name="partner"
                  type="radio"
                  component={UnsetRadio}
                  disabled={partners.size === 0}
                  label="Unset"
                />
              </Grid>
            )}
          </Grid>
          <TextField
            placeholder={`Search ${isCompany ? 'nonprofits' : 'corporate partners'} by name...`}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            style={{ width: '100%' }}
            name={`${isCompany ? 'nonprofit-search' : 'partner-search'}`}
          />

          <List style={{ maxHeight: 300, overflow: 'auto' }}>
            {searchValue.length > 2 &&
              partners.size === 0 &&
              !nonprofitsLoading &&
              !partnersLoading &&
              nonPartnerNonprofits.length === 0 && <p>No results found</p>}
            {(nonprofitsLoading || partnersLoading) && (
              <div style={{ textAlign: 'center', marginTop: 20, marginBottom: 20 }}>
                <CircularProgress />
              </div>
            )}
            {isCompany && !nonprofitsLoading && !partnersLoading && partners.size > 0 && (
              <p>Partner nonprofits</p>
            )}
            {!nonprofitsLoading &&
              !partnersLoading &&
              partners
                .map((partnerNonprofitOrCompany) => (
                  <Field
                    key={partnerNonprofitOrCompany.id}
                    name={isCompany ? 'organization' : 'partner'}
                    type="radio"
                    component={ListFormRadio}
                    partner={partnerNonprofitOrCompany}
                    value={partnerNonprofitOrCompany.id}
                    validate={validateOrganization}
                  />
                ))
                .toArray()}
            {isCompany &&
              !nonprofitsLoading &&
              !partnersLoading &&
              nonPartnerNonprofits.length > 0 && (
                <>
                  <p>Non-partner nonprofits</p>
                  {nonPartnerNonprofits.map((nonPartnerNonprofit) => (
                    <Field
                      key={nonPartnerNonprofit.id}
                      name="organization"
                      type="radio"
                      component={ListFormRadio}
                      value={nonPartnerNonprofit.id}
                      partner={nonPartnerNonprofit}
                      parse={() => nonPartnerNonprofit}
                      format={(value) => value?.id}
                      validate={validateOrganization}
                    />
                  ))}
                </>
              )}
          </List>
          {isCompany &&
            !nonprofitsLoading &&
            !partnersLoading &&
            !nonPartnerNonprofits.length &&
            partners.size === 0 && (
              <Field
                name="organization"
                type="input"
                component={HiddenInput}
                validate={validateOrganization}
              />
            )}
          {isCompany && user?.hasPrivilege(Privileges.PARTNER_MANAGE) && (
            <p>
              If a non-partner nonprofit is selected it will be added as your partner once your deed
              is published
            </p>
          )}
        </Grid>
        {isCompany && (
          <Grid item md={6}>
            <FormFieldLabel style={{ marginTop: 20 }}>Selected nonprofit</FormFieldLabel>
            {user.isAdmin && selectedNonprofit && newPartnerNonprofitSelected && (
              <FormLabel error style={{ fontSize: 12, marginTop: 5, display: 'block' }}>
                Please unset selected partner nonprofit to create new one
              </FormLabel>
            )}
            {selectedNonprofit && (
              <Field
                name="organization"
                type="radio"
                component={SelectedNonprofit}
                nonprofit={selectedNonprofit}
                validate={validateOrganization}
                user={user}
              />
            )}
            {user.isAdmin && (
              <>
                <div>
                  {!selectedNonprofit && <br />}
                  Don&apos;t see them listed? Please enter the nonprofit&apos;s information below.
                </div>
                {!newPartnerNonprofitSelected && (
                  <Button
                    type="button"
                    onClick={() => setNewPartnerNonprofitSelected(!newPartnerNonprofitSelected)}
                    color="primary"
                    variant="contained"
                    size="small"
                    style={{ marginTop: 15, paddingRight: 15 }}
                  >
                    <AddIcon /> Add New Partner
                  </Button>
                )}
              </>
            )}
            {user.isAdmin && newPartnerNonprofitSelected && (
              <div
                style={{
                  border: '1px solid rgba(0, 0, 0, 0.2)',
                  padding: 20,
                  marginTop: 20,
                  position: 'relative',
                }}
              >
                <div
                  style={{
                    paddingRight: 10,
                    position: 'absolute',
                    top: 0,
                    right: 0,
                  }}
                >
                  <IconButton
                    title="Remove new non profit entry"
                    onClick={() => {
                      dispatch(change(partnerName.meta?.form, 'partnerName', ''));
                      dispatch(change(partnerEmail.meta?.form, 'partnerEmail', ''));
                      setNewPartnerNonprofitSelected(!newPartnerNonprofitSelected);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </div>
                <NewPartner />
              </div>
            )}
          </Grid>
        )}
      </Grid>
      {isCompany && organization.meta?.error && organization.meta?.touched && (
        <FormLabel error style={{ fontSize: 12, marginTop: 5 }}>
          {organization.meta?.error}
        </FormLabel>
      )}
    </>
  );
};
FormPartnerSelector.propTypes = {
  partners: PropTypes.object,
  partner: PropTypes.object,
  partnerName: PropTypes.object,
  partnerEmail: PropTypes.object,
  fetchPartnersAction: PropTypes.func,
  partnersLoading: PropTypes.bool,
  required: PropTypes.bool,
  isCompany: PropTypes.bool,
  organization: PropTypes.object,
  acceptsDonations: PropTypes.bool,
  selectedOrganization: PropTypes.object,
  user: PropTypes.object,
  initialSelectedLocation: PropTypes.string,
  initialSelectedSearchTerm: PropTypes.string,
};
export default FormPartnerSelector;
